import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { 
  Plus, 
  Trash2, 
  GripVertical,
  Star,
  Save,
  Edit3,
  Eye
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';
interface GenerationDisplayProps {
  questions: any[];
  answers?: Record<string, any>;
  isEditing?: boolean;
  onQuestionChange?: (index: number, field: string, value: any) => void;
  onAnswerChange?: (questionId: string, value: any) => void;
  onAddQuestion?: () => void;
  onDeleteQuestion?: (index: number) => void;
  onDragEnd?: (result: any) => void;
  onSave?: () => void;
  isSaving?: boolean;
  hasChanges?: boolean;
  mode?: 'view' | 'edit' | 'answer';
}

const GenerationDisplay: React.FC<GenerationDisplayProps> = ({
  questions,
  answers = {},
  isEditing = false,
  onQuestionChange,
  onAnswerChange,
  onAddQuestion,
  onDeleteQuestion,
  onDragEnd,
  onSave,
  isSaving = false,
  hasChanges = false,
  mode = 'view'
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const { t } = useTranslation();
  const handleQuestionEdit = (index: number, field: string, value: any) => {
    if (onQuestionChange) {
      onQuestionChange(index, field, value);
    }
  };

  const handleOptionChange = (questionIndex: number, optionIndex: number, value: string) => {
    if (onQuestionChange) {
      const question = questions[questionIndex];
      const options = [...(question.options || [])];
      options[optionIndex] = value;
      onQuestionChange(questionIndex, 'options', options);
    }
  };

  const handleAddOption = (questionIndex: number) => {
    if (onQuestionChange) {
      const question = questions[questionIndex];
      const options = [...(question.options || [])];
      options.push('');
      onQuestionChange(questionIndex, 'options', options);
    }
  };

  const handleDeleteOption = (questionIndex: number, optionIndex: number) => {
    if (onQuestionChange) {
      const question = questions[questionIndex];
      const options = [...(question.options || [])];
      options.splice(optionIndex, 1);
      onQuestionChange(questionIndex, 'options', options);
    }
  };

  const renderAnswerInput = (question: any, index: number) => {
    const { type, options, scale, id } = question;
    const currentAnswer = answers[id];
    
    switch (type) {
      case 'scale':
      case 'likert':
        return (
          <div className="space-y-4">
            <div className="flex justify-between text-xs md:text-sm text-muted-foreground">
              <span>Strongly Disagree</span>
              <span>Strongly Agree</span>
            </div>
            <RadioGroup 
              value={currentAnswer?.toString() || ""} 
              onValueChange={(value) => onAnswerChange?.(id, parseInt(value))}
              className="flex justify-between"
            >
              {[1, 2, 3, 4, 5].map(num => (
                <div key={num} className="flex flex-col items-center space-y-2">
                  <RadioGroupItem 
                    value={num.toString()} 
                    id={`${id}-${num}`}
                    className="w-5 h-5 md:w-6 md:h-6"
                  />
                  <Label htmlFor={`${id}-${num}`} className="text-xs md:text-sm cursor-pointer">
                    {num}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );
      
      case 'rating':
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4, 5].map(num => (
                <button
                  key={num}
                  onClick={() => onAnswerChange?.(id, num)}
                  className={`w-7 h-7 md:w-8 md:h-8 transition-colors ${
                    currentAnswer >= num 
                      ? 'text-yellow-500 fill-current' 
                      : 'text-muted-foreground hover:text-yellow-400'
                  }`}
                >
                  <Star className="w-full h-full" />
                </button>
              ))}
              {currentAnswer && (
                <span className="ml-2 text-xs md:text-sm text-muted-foreground">
                  {currentAnswer} out of 5
                </span>
              )}
            </div>
          </div>
        );

      case 'slider':
        return (
          <div className="space-y-4">
            <div className="px-1 md:px-2">
              <Slider
                value={[currentAnswer || 50]}
                onValueChange={(value) => onAnswerChange?.(id, value[0])}
                max={100}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs md:text-sm text-muted-foreground mt-2">
                <span>0</span>
                <span className="font-medium">{currentAnswer || 50}</span>
                <span>100</span>
              </div>
            </div>
          </div>
        );
      
      case 'multiple_choice':
        return (
          <div className="space-y-3">
            {options?.map((option: string, optionIndex: number) => (
              <div key={optionIndex} className="flex items-center space-x-3">
                <Checkbox
                  id={`${id}-${optionIndex}`}
                  checked={currentAnswer?.includes(option) || false}
                  onCheckedChange={(checked) => {
                    const newAnswers = currentAnswer || [];
                    if (checked) {
                      onAnswerChange?.(id, [...newAnswers, option]);
                    } else {
                      onAnswerChange?.(id, newAnswers.filter((a: string) => a !== option));
                    }
                  }}
                />
                <Label htmlFor={`${id}-${optionIndex}`} className="text-sm">
                  {option}
                </Label>
              </div>
            ))}
          </div>
        );
      
      default:
        return (
          <div className="space-y-2">
            <div className="p-3 md:p-4 rounded-md bg-muted/40">
              <div className="h-8 bg-muted/60 rounded" />
            </div>
            <span className="text-xs md:text-sm text-muted-foreground italic">
              Response area for {type} question...
            </span>
          </div>
        );
    }
  };

  const renderAnswerPlaceholder = (question: any) => {
    const { type, options } = question;
    
    const normalizedType = type?.toLowerCase().replace(/[^a-z]/g, '');
    
    switch (normalizedType) {
      case 'scale':
      case 'likert':
      case 'ratingscale110':
      case 'ratingscale15':
      case 'ratingscale':
        return (
          <div className="space-y-4">
            <div className="flex justify-between text-sm font-medium text-muted-foreground">
              <span>Strongly Disagree</span>
              <span>Neutral</span>
              <span>Strongly Agree</span>
            </div>
            <div className="flex justify-between items-center px-2">
              {[1, 2, 3, 4, 5].map(num => (
                <div key={num} className="flex flex-col items-center space-y-2 group">
                  <div className="w-6 h-6 border-2 border-primary/30 rounded-full bg-background hover:border-primary transition-colors cursor-pointer group-hover:scale-110 transform duration-200" />
                  <span className="text-sm font-medium text-primary/70 group-hover:text-primary transition-colors">{num}</span>
                </div>
              ))}
            </div>
            <div className="text-center">
              <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded">
                Click to select your agreement level
              </span>
            </div>
          </div>
        );
      
      case 'rating':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-center space-x-1">
              {[1, 2, 3, 4, 5].map(num => (
                <Star 
                  key={num} 
                  className="w-10 h-10 text-yellow-400/40 hover:text-yellow-400 transition-colors cursor-pointer transform hover:scale-110 duration-200" 
                  fill="currentColor"
                />
              ))}
            </div>
            <div className="text-center">
              <span className="text-sm font-medium text-muted-foreground">Rate your experience</span>
              <div className="text-xs text-muted-foreground mt-1 bg-muted/50 px-2 py-1 rounded inline-block">
                Click stars to rate from 1 to 5
              </div>
            </div>
          </div>
        );

      case 'slider':
        return (
          <div className="space-y-4">
            <div className="px-3">
              <div className="relative">
                <div className="h-3 bg-gradient-to-r from-red-200 via-yellow-200 to-green-200 rounded-full">
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="w-6 h-6 bg-primary border-4 border-background rounded-full shadow-lg cursor-pointer hover:scale-110 transition-transform duration-200" />
                  </div>
                </div>
              </div>
              <div className="flex justify-between text-sm font-medium text-muted-foreground mt-3">
                <span>0</span>
                <span className="bg-primary/10 text-primary px-2 py-1 rounded text-xs">50</span>
                <span>100</span>
              </div>
            </div>
            <div className="text-center">
              <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded">
                Drag the slider to select a value
              </span>
            </div>
          </div>
        );
      
      case 'multiplechoice':
      case 'multiple':
      case 'checkbox':
        const multipleChoiceOptions = options && options.length > 0 ? options : [
          'Excellent',
          'Good', 
          'Average',
          'Poor'
        ];
        
        return (
          <div className="space-y-3">
            {multipleChoiceOptions.map((option: string, optionIndex: number) => (
              <div key={optionIndex} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group">
                <div className="w-5 h-5 border-2 border-primary/30 rounded bg-background group-hover:border-primary transition-colors" />
                <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{option}</span>
              </div>
            ))}
            <div className="text-center mt-4">
              <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded">
                Select multiple options
              </span>
            </div>
          </div>
        );
      
      case 'singlechoice':
      case 'single':
      case 'radio':
      case 'yesno':
      case 'boolean':
        let singleChoiceOptions;
        
        if (normalizedType === 'yesno' || normalizedType === 'boolean') {
          singleChoiceOptions = options && options.length > 0 ? options : ['Yes', 'No'];
        } else {
          singleChoiceOptions = options && options.length > 0 ? options : [
            'Strongly Agree',
            'Agree', 
            'Neutral',
            'Disagree',
            'Strongly Disagree'
          ];
        }
        
        return (
          <div className="space-y-3">
            {singleChoiceOptions.map((option: string, optionIndex: number) => (
              <div key={optionIndex} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group">
                <div className="w-5 h-5 border-2 border-primary/30 rounded-full bg-background group-hover:border-primary transition-colors" />
                <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{option}</span>
              </div>
            ))}
            <div className="text-center mt-4">
              <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded">
                Select one option
              </span>
            </div>
          </div>
        );
      
      case 'text':
        return (
          <div className="relative">
            <div className="border-2 border-dashed border-primary/20 rounded-lg p-4 bg-gradient-to-r from-background to-muted/20 hover:border-primary/40 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-4 bg-primary/30 rounded" />
                <span className="text-xs font-medium text-muted-foreground">Short Text Response</span>
              </div>
              <span className="text-sm text-muted-foreground italic">
                Type your answer here...
              </span>
            </div>
          </div>
        );
      
      case 'long_text':
        return (
          <div className="relative">
            <div className="border-2 border-dashed border-primary/20 rounded-lg p-4 bg-gradient-to-br from-background to-muted/20 min-h-[120px] hover:border-primary/40 transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-4 bg-primary/30 rounded" />
                <span className="text-xs font-medium text-muted-foreground">Detailed Response Area</span>
              </div>
              <div className="space-y-2">
                <span className="text-sm text-muted-foreground italic block">
                  Please provide a detailed response...
                </span>
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-muted-foreground/20 rounded" />
                  <div className="w-2 h-2 bg-muted-foreground/15 rounded" />
                  <div className="w-2 h-2 bg-muted-foreground/10 rounded" />
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="border-2 border-dashed border-muted-foreground/20 rounded-lg p-4 bg-muted/10">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1 h-4 bg-muted-foreground/30 rounded" />
              <span className="text-xs font-medium text-muted-foreground capitalize">{type} Question</span>
            </div>
            <span className="text-sm text-muted-foreground italic">
              Response area for {type} question...
            </span>
          </div>
        );
    }
  };

  const renderQuestionCard = (question: any, index: number) => {
    const isCurrentlyEditing = editingIndex === index;

    if (mode === 'edit' || isCurrentlyEditing) {
      return (
        <Card key={question.id || index} className="border-l-4 border-l-primary/20">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {mode === 'edit' && onDragEnd && (
                  <GripVertical className="w-4 h-4 text-muted-foreground cursor-move" />
                )}
                <span className="text-sm font-medium">Question {index + 1}</span>
                <Badge variant="outline" className="text-xs">
                  {question.type}
                </Badge>
              </div>
              <div className="flex gap-2">
                {mode !== 'edit' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingIndex(isCurrentlyEditing ? null : index)}
                  >
                    {isCurrentlyEditing ? <Eye className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                  </Button>
                )}
                {onDeleteQuestion && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteQuestion(index)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium">Question Text</label>
              <Textarea
                value={question.question || ''}
                onChange={(e) => handleQuestionEdit(index, 'question', e.target.value)}
                placeholder="Enter your question..."
                rows={2}
              />
            </div>

            {(question.type === 'multiple_choice' || question.type === 'single_choice') && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Options</label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAddOption(index)}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Option
                  </Button>
                </div>
                <div className="space-y-2">
                  {(question.options || []).map((option: string, optionIndex: number) => (
                    <div key={optionIndex} className="flex gap-2">
                      <Input
                        value={option}
                        onChange={(e) => handleOptionChange(index, optionIndex, e.target.value)}
                        placeholder={`Option ${optionIndex + 1}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteOption(index, optionIndex)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    return (
      <Card key={question.id || index} className="border-l-4 border-l-primary/30 hover:border-l-primary transition-colors shadow-sm">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-start gap-3 md:gap-4">
            <div className="w-7 h-7 md:w-8 md:h-8 bg-primary/10 text-primary rounded-full flex items-center justify-center font-semibold text-xs md:text-sm flex-shrink-0 mt-1">
              {index + 1}
            </div>
            
            <div className="flex-1 space-y-3 md:space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="text-[10px] md:text-xs">
                    {question.category || question.type}
                  </Badge>
                  <Badge variant="secondary" className="text-[10px] md:text-xs">
                    {question.type}
                  </Badge>
                  {mode === 'view' && onQuestionChange && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingIndex(index)}
                      className="ml-auto"
                    >
                      <Edit3 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                <h3 className="text-base md:text-lg font-medium text-foreground mb-3 md:mb-4">
                  {question.question}
                </h3>
              </div>
              
              <div className="bg-muted/30 rounded-lg p-3 md:p-4">
                {mode === 'answer' ? renderAnswerInput(question, index) : renderAnswerPlaceholder(question)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const questionsContent = (
    <div className="space-y-3 md:space-y-4">
      {questions.map((question, index) => {
        if (mode === 'edit' && onDragEnd) {
          return (
            <Draggable
              key={question.id || index}
              draggableId={String(question.id || index)}
              index={index}
            >
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                >
                  {renderQuestionCard(question, index)}
                </div>
              )}
            </Draggable>
          );
        }
        return renderQuestionCard(question, index);
      })}
    </div>
  );

  return (
    <div className="space-y-3 md:space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          {mode === 'edit' && hasChanges && (
            <Badge variant="secondary" className="ml-0">{t('results.surveyTemplateResults.questionEditMode.unsavedChanges')}</Badge>
          )}
        </div>
        <div className="flex gap-2">
          {mode === 'edit' && onSave && (
            <Button 
              onClick={onSave} 
              disabled={!hasChanges || isSaving}
              size="sm"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? t('results.surveyTemplateResults.questionEditMode.saving') : t('results.surveyTemplateResults.questionEditMode.saveChanges')}
            </Button>
          )}
          {onAddQuestion && (
            <Button onClick={onAddQuestion} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              {t('results.surveyTemplateResults.questionEditMode.addQuestion')}
            </Button>
          )}
        </div>
      </div>
      <div>
        {mode === 'edit' && onDragEnd ? (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="questions">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {questionsContent}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          questionsContent
        )}
        
        {questions.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <p>No questions yet. {onAddQuestion ? 'Click "Add Question" to get started.' : 'This generation has no questions.'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerationDisplay;